import axios from 'axios';
import axiosInstance from '../axiosInstance';

/**
 * 워크샵 목록 조회
 */
export const getWorkshopList = async ({
  category,
  featured,
  hit,
  free,
} = {}) => {
  try {
    const response = await axiosInstance.get('/workshops', {
      params: {
        ...(category && { category }),
        ...(featured !== undefined && { featured }),
        ...(hit !== undefined && { hit }),
        ...(free !== undefined && { free }),
      },
    });
    return response.data.data;
  } catch (error) {
    console.error('Get Workshop List API Error:', error);
    throw error;
  }
};

/**
 * 워크샵 상세 조회
 */
/**
 * 같은 워크숍을 다시 예약할 때 모달에 미리 채울 직전 답변.
 * @returns {Promise<object>} questionId → 답변. 예약한 적이 없으면 {}
 */
export const getMyWorkshopAnswers = async (workshopId) => {
  try {
    const response = await axiosInstance.get(
      `/workshops/${workshopId}/my-answers`,
      { skipLoading: true },
    );
    return response.data.data ?? {};
  } catch (error) {
    // 채워주는 건 편의 기능이라, 실패해도 빈칸으로 진행한다
    console.error('Get My Workshop Answers API Error:', error);
    return {};
  }
};

export const getWorkshopDetail = async (workshopId) => {
  try {
    const response = await axiosInstance.get(`/workshops/${workshopId}`);

    const etag = response.headers?.etag;
    if (etag) sessionStorage.setItem(`etag:/workshops/${workshopId}`, etag);

    return response.data.data;
  } catch (error) {
    console.error('Get Workshop Detail API Error:', error);
    throw error;
  }
};

/**
 * 워크샵 등록 (어드민 전용)
 */
export const createWorkshop = async (data) => {
  try {
    const response = await axiosInstance.post('/workshops', data, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data.data;
  } catch (error) {
    console.error('Create Workshop API Error:', error);
    throw error;
  }
};

export const getWorkshopImageUploadUrl = async (fileName) => {
  const response = await axiosInstance.post(
    '/workshops/admin/images/upload-url',
    null,
    { params: { fileName } },
  );
  return response.data.data;
};

export const uploadWorkshopImageToS3 = async (file, uploadUrl) => {
  await axios.put(uploadUrl, file, {
    headers: {
      'Content-Type': file.type,
    },
  });
};

/**
 * 워크샵 수정 (어드민 전용)
 */
export const updateWorkshop = async (workshopId, data) => {
  try {
    const response = await axiosInstance.put(`/workshops/${workshopId}`, data, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data.data;
  } catch (error) {
    console.error('Update Workshop API Error:', error);
    throw error;
  }
};

/**
 * 워크샵 삭제 (어드민 전용)
 */
export const deleteWorkshop = async (workshopId) => {
  try {
    const response = await axiosInstance.delete(`/workshops/${workshopId}`);
    return response.data;
  } catch (error) {
    console.error('Delete Workshop API Error:', error);
    throw error;
  }
};

/**
 * 최신 워크샵 3개 조회
 */
export const getRecentWorkshops = async () => {
  try {
    const response = await axiosInstance.get('/workshops/recent');
    return response.data.data;
  } catch (error) {
    console.error('Get Recent Workshops API Error:', error);
    throw error;
  }
};

/**
 * 워크샵 슬롯 어드민용 조회 (대표이미지, 날짜/시간, Category, 제목, Waitlist 수)
 */
export const getWorkshopSlotAdminList = async ({
  page = 0,
  size = 12,
  sortBy,
  dateRange,
  category,
  status,
} = {}) => {
  try {
    const response = await axiosInstance.get('/workshops/admin', {
      params: {
        page,
        size,
        ...(sortBy && { sortBy }),
        ...(dateRange && dateRange !== 'All time' && { dateRange }),
        ...(category && category !== 'All categories' && { category }),
        ...(status && status !== 'All Statuses' && { status }),
      },
    });
    return response.data.data;
  } catch (error) {
    console.error('Get Workshop Slot Admin API Error:', error);
    throw error;
  }
};

/**
 * 삭제된 워크샵 슬롯 어드민용 조회
 */
export const getDeletedWorkshopSlotAdminList = async ({
  page = 0,
  size = 12,
  sortBy,
  dateRange,
  category,
  status,
} = {}) => {
  try {
    const response = await axiosInstance.get('/workshops/admin/deleted', {
      params: {
        page,
        size,
        ...(sortBy && { sortBy }),
        ...(dateRange && dateRange !== 'All time' && { dateRange }),
        ...(category && category !== 'All categories' && { category }),
        ...(status && status !== 'All Statuses' && { status }),
      },
    });
    return response.data.data;
  } catch (error) {
    console.error('Get Deleted Workshop Slot Admin API Error:', error);
    throw error;
  }
};

/**
 * 워크샵 슬롯 Soft 삭제 (어드민 전용)
 */
export const softDeleteWorkshopSlots = async (slotIds) => {
  try {
    const response = await axiosInstance.delete('/workshops/admin/slots/soft', {
      data: { slotIds },
    });
    return response.data;
  } catch (error) {
    console.error('Soft Delete Workshop Slots API Error:', error);
    throw error;
  }
};

/**
 * 워크샵 슬롯 영구 삭제 (어드민 전용)
 */
export const permanentlyDeleteWorkshopSlots = async (slotIds) => {
  try {
    const response = await axiosInstance.delete('/workshops/admin/slots', {
      data: { slotIds },
    });
    return response.data;
  } catch (error) {
    console.error('Permanently Delete Workshop Slots API Error:', error);
    throw error;
  }
};

/**
 * 워크샵 슬롯 상태 변경 (어드민 전용)
 */
export const updateSlotStatus = async (slotId, status) => {
  try {
    const response = await axiosInstance.patch(
      `/workshops/admin/slots/${slotId}/status`,
      { status },
    );
    return response.data;
  } catch (error) {
    console.error('Update Slot Status API Error:', error);
    throw error;
  }
};

/**
 * 워크샵 슬롯 노출 상태 변경 (어드민 전용)
 */
export const updateSlotHidden = async (slotId, hidden) => {
  try {
    const response = await axiosInstance.patch(
      `/workshops/admin/slots/${slotId}/hidden`,
      { hidden },
    );
    return response.data;
  } catch (error) {
    console.error('Update Slot Hidden API Error:', error);
    throw error;
  }
};

/**
 * 워크샵 슬롯 상세 조회 (어드민 전용)
 */
export const getSlotDetail = async (slotId) => {
  try {
    const response = await axiosInstance.get(
      `/workshops/admin/slots/${slotId}/detail`,
    );
    return response.data.data;
  } catch (error) {
    console.error('Get Slot Detail API Error:', error);
    throw error;
  }
};

/**
 * 워크샵 슬롯 취소 마감 설정 (어드민 전용, DL-001)
 * @param {number} slotId
 * @param {string|null} cancellationDeadline - ISO-8601 문자열, null이면 마감 해제
 */
export const updateSlotCancellationDeadline = async (
  slotId,
  cancellationDeadline,
) => {
  try {
    const response = await axiosInstance.patch(
      `/workshops/admin/slots/${slotId}/cancellation-deadline`,
      { cancellationDeadline },
    );
    return response.data;
  } catch (error) {
    console.error('Update Slot Cancellation Deadline API Error:', error);
    throw error;
  }
};

/**
 * 워크샵 USD·KRW 가격 동시 갱신 (어드민 전용, DL-014)
 * 두 값 모두 필수 — 한쪽만 보내면 백엔드에서 차단된다.
 * @param {number} workshopId
 * @param {number} priceUsd
 * @param {number} priceKrw
 */
export const updateWorkshopPrice = async (workshopId, priceUsd, priceKrw) => {
  try {
    const response = await axiosInstance.patch(
      `/workshops/admin/${workshopId}/price`,
      { priceUsd, priceKrw },
    );
    return response.data;
  } catch (error) {
    console.error('Update Workshop Price API Error:', error);
    throw error;
  }
};
